import log from 'loglevel';

import { redact } from '@/api/redact';
import { env } from '@/config/env';

/**
 * The only logging path in the app. `no-console` is an error everywhere with no allowances.
 *
 * Redaction happens **inside** this module rather than at the call site, so an unredacted payload
 * cannot be logged even by a caller who has not read the rules. That is the entire design: the
 * session token is bearer-equivalent and lives in `location.href`, so a single careless
 * `logger.debug('url', location.href)` would hand a shared phone's console a working credential.
 *
 * There is no remote sink — no Sentry, Datadog, or LogRocket (ADR-018). Error reporters capture
 * URLs, and this URL contains the token. Session replay captures the DOM, which here includes a
 * trader's account number and a photograph of a bank receipt.
 */

/**
 * `error` rather than `silent` in production, so loglevel itself suppresses everything below it and
 * the unhandled-boundary line still reaches the console. Routing that one line through a direct
 * `console.error` instead would need a `no-console` exemption and a second code path for the same
 * output.
 *
 * `enableDevTools` is already forced off when `VITE_APP_ENV=production` (see `envSchema.ts`), so
 * debug output cannot be switched on by a mis-set variable in a production build.
 */
log.setLevel(env.enableDevTools ? 'debug' : 'error');

type Ctx = Record<string, unknown> | undefined;

function emit(level: 'debug' | 'info' | 'warn' | 'error', message: string, ctx: Ctx): void {
  if (ctx === undefined) {
    log[level](message);
    return;
  }
  log[level](message, redact(ctx));
}

export const logger = {
  debug: (message: string, ctx?: Record<string, unknown>) => {
    emit('debug', message, ctx);
  },
  info: (message: string, ctx?: Record<string, unknown>) => {
    emit('info', message, ctx);
  },
  warn: (message: string, ctx?: Record<string, unknown>) => {
    emit('warn', message, ctx);
  },
  /**
   * The only level that survives in production, and it should only be reached from the root error
   * boundary. A fatal that leaves no trace is a support ticket nobody can answer.
   */
  error: (message: string, ctx?: Record<string, unknown>) => {
    emit('error', message, ctx);
  },
};
